import type { ImportPreview, ReimportCandidate } from '@testflow/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { type CommitResult, type DiscardedRow, importApi } from '../../lib/import-api.js';
import { ComputationStatus } from './computation-status.js';
import { translateImportError } from './import-errors.js';
import {
  type ReimportChoices,
  ReimportReview,
  carryOverChoices,
  droppedWarningCount,
  initialChoices,
  reimportCounts,
  toDecisions,
} from './reimport-review.js';
import { useImportComputation } from './use-import-computation.js';

// Design-Spec 3.8: Zusammenfassung + Fußzeilen MÜLL-ERKENNUNG (IMP-5) und DUBLETTEN (IMP-6),
// dann Commit (IMP-1 — danach ist das Tool die Quelle der Wahrheit).
//
// Doppel-Commit-Schutz (IMP-1): der eigentliche Schutz sitzt im Backend (Task 15: die Tx setzt
// den Job-Status transaktional zuerst um, ein zweiter Request bekommt import_already_committed).
// Das Frontend darf sich darauf aber NICHT verlassen — zwei Klicks im selben Tick (bevor React
// das `busy`-State-Update gerendert hat) würden sonst trotzdem zwei Requests absetzen: beide
// Aufrufe läsen in diesem Fall denselben veralteten `busy=false`-Wert aus dem Closure. Deshalb
// trägt ein `useRef`-Flag (committingRef) den eigentlichen Guard — Refs mutieren synchron und
// sofort, im Gegensatz zu useState, dessen neuer Wert erst nach dem nächsten Render sichtbar ist.
// `disabled={busy}` am Button bleibt zusätzlich (Defense-in-Depth, UX-Feedback + deckt den
// Normalfall/sequenzielle Klicks ab).

// #110: feste Reihenfolge der Entitätsarten im Warnkasten (Schlüssel = `UntranslatedSummary.by_kind`).
const UNTRANSLATED_KINDS = ['test_case', 'step', 'process', 'e2e'] as const;

export function PreviewStep({
  projectId,
  jobId,
  onCommitted,
  onBack,
  onBackToMapping,
}: {
  projectId: string;
  jobId: string;
  onCommitted: (result: CommitResult) => void;
  // #151: In der fertigen Vorschau sieht der Nutzer, was das Mapping ergeben hat. Fehlt dort eine
  // Spalte, führt dieser Knopf direkt in den Mapping-Schritt — ohne Umweg über „Nutzer“ (onBack).
  onBackToMapping?: () => void;
  // Rueckweg in den vorherigen Schritt. Ohne ihn ist ein Fehler hier eine Sackgasse: die Vorschau
  // ist der einzige Weg zum Commit, und der Wizard-Zustand lebt nur im Speicher dieser Seite.
  onBack?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  // #125: Entscheidungen der Abgleich-Vorschau. Lädt der Admin die Vorschau neu (veralteter Stand),
  // werden sie je Zeile übernommen, soweit die Zeile noch existiert und passt (carryOverChoices) —
  // `carried` blendet dann die Bitte ein, sie zu prüfen. Die Refs tragen den letzten Stand in den
  // Lade-Effekt, ohne ihn bei jeder Entscheidung neu auszulösen.
  const [choices, setChoices] = useState<ReimportChoices | null>(null);
  const [carried, setCarried] = useState(false);
  const choicesRef = useRef<ReimportChoices | null>(null);
  const comparisonRef = useRef<ImportPreview['reimport']>(null);
  choicesRef.current = choices;
  // #125: der gewählte Quell-Import (Dateiname + Datum) — der Wizard ist linear, die Wahl fiel im
  // Upload-Schritt; hier steht sie noch einmal, bevor committet wird.
  const [source, setSource] = useState<ReimportCandidate | null>(null);
  // #125: veralteter Stand (409 reimport_preview_stale) — der Fehler bleibt stehen, bis der Admin
  // die Vorschau selbst neu lädt. Ein stilles Neuladen hätte seine Entscheidungen verworfen.
  const [stale, setStale] = useState(false);
  const [discards, setDiscards] = useState<DiscardedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const committingRef = useRef(false);
  // #96: der Commit ist irreversibel (danach ist das Tool die Quelle der Wahrheit) — der Klick
  // auf "Import committen" öffnet erst die Rückfrage mit der Testfall-Anzahl, `handleCommit`
  // (inkl. committingRef/busy-Guard, s. o.) läuft unverändert erst beim Bestätigen.
  const [confirmCommit, setConfirmCommit] = useState(false);
  // #125 (Q2/Q5): Eine Quell-ID, die schon im Katalog steht oder in der Datei doppelt vorkommt,
  // macht die Neuanlage unmöglich (Unique-Index). Der Server antwortet dann 409 — der Knopf soll
  // gar nicht erst einladen. Im Neuimport zeigt die Abgleich-Vorschau (Task 8) solche Fälle selbst.
  const reimport = preview?.reimport ?? null;
  const sourceRefBlocked =
    reimport === null &&
    (preview?.duplicates ?? []).some((d) => d.reason === 'source_ref_collision');
  const reimportErrors = reimport?.errors ?? [];
  const droppedCount = reimport ? droppedWarningCount(reimport) : null;
  // #125: im Neuimport filtert die API die Katalog-Hinweise zugeordneter Zeilen selbst
  // (reimportDuplicateHints) — was hier ankommt, gehört zu einer echten Neuanlage oder zur Datei.
  const duplicates = preview?.duplicates ?? [];

  // #73: Die Berechnung (KI-Übersetzung) läuft im Hintergrund auf dem Server; der Hook startet sie
  // (oder übernimmt einen fertigen Entwurf), fragt den Fortschritt ab und ruft `loadPreview` genau
  // einmal, sobald sie `done` ist. So hängt keine Anfrage minutenlang offen.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Wird nur über `onDone` (Callback läuft über optionsRef im Hook) und den Neu-Laden-Weg
  // aufgerufen; ein Identitätswechsel dieser Funktion löst daher keinen Refetch aus.
  async function loadPreview(): Promise<void> {
    try {
      const p = await importApi.getPreview(projectId, jobId);
      if (aliveRef.current) {
        const prevChoices = choicesRef.current;
        const prevComparison = comparisonRef.current;
        let next: ReimportChoices | null = null;
        let carry = false;
        if (p.reimport && prevChoices && prevComparison) {
          next = carryOverChoices(p.reimport, prevChoices, prevComparison);
          carry = true;
        } else if (p.reimport) {
          next = initialChoices(p.reimport);
        }
        setPreview(p);
        comparisonRef.current = p.reimport;
        setChoices(next);
        setCarried(carry);
        setStale(false);
        setError(null);
      }
      if (p.reimport) {
        const sourceId = p.reimport.reimport_of_job_id;
        // Nur Anzeige: scheitert das Laden, steht die Zeile ohne Dateinamen da.
        const r = await importApi.getReimportCandidates(projectId, jobId).catch(() => null);
        if (aliveRef.current) {
          setSource(r?.candidates.find((c) => c.import_job_id === sourceId) ?? null);
        }
      }
    } catch (err) {
      if (aliveRef.current) {
        setError(translateImportError(t, err instanceof Error ? err.message : 'preview_failed'));
      }
    }
  }

  const {
    computation,
    error: computationError,
    restart,
  } = useImportComputation({
    start: () => importApi.startPreview(projectId, jobId),
    poll: () => importApi.getComputation(projectId, jobId),
    onDone: () => void loadPreview(),
  });

  // Fehler der Berechnung selbst (HTTP-Fehler von Start/Abfrage) laufen in denselben Fehlerzweig
  // wie Fehler beim Laden der Vorschau.
  const shownError = error ?? (computationError ? translateImportError(t, computationError) : null);

  // Zaehler-Ersatz: jeder Klick auf "Erneut versuchen" startet die Berechnung neu (der Server
  // übernimmt einen fertigen Entwurf ohne Neuübersetzung, der Aufruf ist dann sofort durch).
  function retry(): void {
    setError(null);
    restart();
  }

  async function handleShowDiscards(): Promise<void> {
    try {
      setDiscards(await importApi.getDiscards(projectId, jobId));
    } catch (err) {
      setError(translateImportError(t, err instanceof Error ? err.message : 'discards_failed'));
    }
  }

  async function handleCommit(): Promise<void> {
    if (committingRef.current) return;
    committingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      // #125: ein Neuimport-Job braucht IMMER die Entscheidungen (sonst 400); der Normalimport
      // schickt weiter keinen Body.
      onCommitted(
        reimport && choices
          ? await importApi.commit(projectId, jobId, toDecisions(reimport, choices))
          : await importApi.commit(projectId, jobId),
      );
    } catch (err) {
      const code = err instanceof Error ? err.message : 'commit_failed';
      if (code === 'reimport_preview_stale') setStale(true);
      setError(translateImportError(t, code));
      // #73: Der Entwurf fehlt, weil eine Eingabe ihn nach dieser Vorschau verworfen hat — der
      // Server baut ihn beim Commit nicht mehr selbst. Neu berechnen statt Sackgasse: der Fortschritt
      // erscheint über der alten Vorschau, `loadPreview` holt danach den neuen Stand (und löscht den
      // Hinweis). `stale` sperrt den Commit schon, bevor startPreview antwortet — bis dahin ist der
      // Berechnungszustand leer, und der Knopf wäre sonst kurz klickbar (Review T6-2).
      if (code === 'import_draft_missing') {
        setStale(true);
        restart();
      }
    } finally {
      committingRef.current = false;
      setBusy(false);
      setConfirmCommit(false);
    }
  }

  // #73: Läuft eine Neuberechnung (z. B. „Vorschau neu laden" nach 409 reimport_preview_stale) oder
  // ist sie gescheitert, obwohl schon eine Vorschau geladen ist, gehört der Status ÜBER die alte
  // Vorschau — sonst bliebe der Commit ohne Erklärung gesperrt. Der Commit bleibt gesperrt (`stale`),
  // bis `loadPreview` die neue Vorschau geladen hat.
  const recomputing =
    computation && ['running', 'failed', 'interrupted'].includes(computation.state ?? '');

  if (shownError && !preview) {
    return (
      <div className="max-w-xl">
        <p role="alert" className="text-xs text-status-fail">
          {shownError}
        </p>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={retry}
            className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white"
          >
            {t('import.preview.retry')}
          </button>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-btn border border-[var(--border)] px-4 py-2 text-sm text-text-body"
            >
              {t('import.preview.back')}
            </button>
          )}
        </div>
        <p className="mt-3 text-[11px] text-text-muted">{t('import.preview.retryHint')}</p>
      </div>
    );
  }
  if (!preview) {
    return (
      <div>
        {recomputing ? (
          <ComputationStatus computation={computation} kind="preview" onRestart={restart} />
        ) : (
          <div className="text-text-muted">
            <p>{t('common.loading')}</p>
            <p className="mt-1 text-[11px]">{t('import.preview.loadingHint')}</p>
          </div>
        )}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mt-4 rounded-btn border border-[var(--border)] px-4 py-2 text-sm text-text-body"
          >
            {t('import.preview.back')}
          </button>
        )}
      </div>
    );
  }

  const untranslated = preview.untranslated;
  // #110: der Server legt den Grund auch in `warnings` ab; im Warnkasten steht er schon.
  const generalWarnings = untranslated
    ? preview.warnings.filter((w) => w !== untranslated.reason)
    : preview.warnings;

  return (
    // #125: die Abgleich-Tabelle (TestFlow | Datei) braucht Breite — am Zielschirm 1280×800 bleibt
    // so neben der Navigation genug Platz für beide Spalten.
    <div className={reimport ? 'max-w-5xl' : 'max-w-3xl'}>
      {recomputing && (
        <div className="mb-4">
          <ComputationStatus computation={computation} kind="preview" onRestart={restart} />
        </div>
      )}
      {/* #110: Felder ohne Übersetzung (z. B. Ratenlimit) stehen oberhalb der Kennzahlen, nicht in
          der Sammel-Warnliste — sonst übersieht man vor dem Commit, dass Texte fehlen. */}
      {untranslated && (
        <div
          role="alert"
          className="mb-4 rounded-card border border-status-warn bg-bg-surface p-4 text-xs text-status-warn"
        >
          <p className="text-sm font-medium">
            {t('import.preview.untranslated.title', { count: untranslated.fields })}
          </p>
          <ul className="mt-1 list-disc pl-4">
            {UNTRANSLATED_KINDS.filter((k) => untranslated.by_kind[k] > 0).map((k) => (
              <li key={k}>
                {t(`import.preview.untranslated.${k}`, { count: untranslated.by_kind[k] })}
              </li>
            ))}
          </ul>
          <p className="mt-2">{untranslated.reason}</p>
        </div>
      )}
      <div className="rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <p className="text-sm text-text-primary">
          {t('import.preview.summary', {
            steps: preview.steps,
            testCases: preview.test_cases,
            processes: preview.processes,
            e2e: preview.e2e,
          })}
        </p>
        <p className="mt-2 text-xs text-text-body">
          {t('import.preview.translations', { count: preview.translations })}
        </p>
        {preview.review_flags > 0 && (
          <p className="mt-1 text-xs text-status-warn">
            {t('import.preview.reviewFlags', { count: preview.review_flags })}
          </p>
        )}
      </div>

      <div className="mt-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <p className="text-[11px] uppercase tracking-wide text-text-muted">
          {t('import.preview.junkTitle', { count: preview.discarded_rows })}
        </p>
        <button
          type="button"
          onClick={() => void handleShowDiscards()}
          className="mt-2 text-xs text-brand-accent underline"
        >
          {t('import.preview.showDiscards')}
        </button>
        {discards && (
          <ul className="mt-2 max-h-48 overflow-y-auto text-[11px] text-text-body">
            {discards.map((d) => (
              <li
                key={`${d.sheet_name}-${d.row_no}`}
                className="border-t border-[var(--border)] py-1"
              >
                <span className="font-mono">
                  {d.sheet_name}:{d.row_no}
                </span>{' '}
                · {d.reason} · {Object.values(d.cells).filter(Boolean).join(' | ')}
              </li>
            ))}
          </ul>
        )}
      </div>

      {duplicates.length > 0 && (
        <div className="mt-4 rounded-card border border-[var(--border)] bg-bg-surface p-4">
          <p className="text-[11px] uppercase tracking-wide text-status-warn">
            {t('import.preview.duplicatesTitle', { count: duplicates.length })}
          </p>
          <ul className="mt-2 text-xs text-text-body">
            {duplicates.map((d) => (
              <li key={`${d.kind}-${d.label}-${d.reason}-${d.against}`}>
                {t('import.preview.duplicate', {
                  label: d.label,
                  reason: t(`import.preview.reasons.${d.reason}`),
                  against: t(`import.preview.against.${d.against}`),
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {generalWarnings.length > 0 && (
        <ul className="mt-4 text-xs text-status-warn">
          {/* I2: Index statt Warntext als Key — der Text selbst ist kein stabiler eindeutiger
              Schluessel (z. B. mehrere "N Zeilen ohne Testfall-Zuordnung..."-Meldungen fuer
              verschiedene Blatt-Typen koennen zufaellig gleich lang/aehnlich, im Extremfall sogar
              identisch sein). Index reicht, weil diese Liste nie umsortiert wird. */}
          {generalWarnings.map((w, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Liste ist statisch (kein Reorder/Filter)
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}

      {reimport && (
        <p className="mt-4 text-sm text-text-primary">
          {source
            ? t('import.reimport.sourceLine', {
                filename: source.filename,
                date: new Date(source.committed_at).toLocaleDateString(i18n.language),
              })
            : t('import.reimport.sourceLineUnknown')}
        </p>
      )}

      {reimportErrors.length > 0 && (
        <div role="alert" className="mt-4 text-xs text-status-fail">
          <p>{t('import.reimport.errorsTitle')}</p>
          <ul className="mt-1 list-disc pl-4">
            {reimportErrors.map((e) => (
              <li key={`${e.code}-${e.label}`}>
                {t(`import.reimport.error.${e.code}`, {
                  label: e.label,
                  humanId: e.human_id ?? '',
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {reimport && droppedCount !== null && (
        <output className="mt-4 block rounded-card border border-status-warn p-3 text-sm font-medium text-status-warn">
          {t('import.reimport.manyDropped', { n: droppedCount })}
        </output>
      )}
      {reimport && choices && carried && (
        <output className="mt-4 block text-xs text-status-warn">
          {t('import.reimport.choicesCarried')}
        </output>
      )}
      {reimport && choices && (
        <ReimportReview comparison={reimport} choices={choices} onChange={setChoices} />
      )}

      {shownError && (
        <div className="mt-4 flex items-center gap-3">
          <p role="alert" className="text-xs text-status-fail">
            {shownError}
          </p>
          {stale && (
            <button
              type="button"
              onClick={retry}
              className="rounded-btn border border-[var(--border)] px-3 py-1 text-xs text-text-body"
            >
              {t('import.reimport.reloadPreview')}
            </button>
          )}
        </div>
      )}

      {sourceRefBlocked && (
        <p role="alert" className="mt-4 text-xs text-status-fail">
          {t('import.preview.sourceRefBlocked')}
        </p>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          // #73: Läuft eine Neuberechnung oder ist sie gescheitert, gibt es keinen gültigen Entwurf —
          // ein Commit liefe nur in import_draft_missing.
          disabled={busy || sourceRefBlocked || reimportErrors.length > 0 || stale || !!recomputing}
          onClick={() => setConfirmCommit(true)}
          className="rounded-btn bg-brand-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {t('import.preview.commit')}
        </button>
        {onBackToMapping && (
          <button
            type="button"
            // #151: Während des Commits darf der Wizard nicht in einen früheren Schritt springen.
            disabled={busy}
            onClick={onBackToMapping}
            className="rounded-btn border border-[var(--border)] px-4 py-2 text-sm text-text-body disabled:opacity-50"
          >
            {t('import.preview.backToMapping')}
          </button>
        )}
        <span className="text-[11px] text-text-muted">
          {t(reimport ? 'import.reimport.commitHint' : 'import.preview.commitHint')}
        </span>
      </div>

      {confirmCommit && (
        <ConfirmDialog
          title={t('import.preview.commit')}
          message={
            reimport && choices
              ? t('import.reimport.confirm', { ...reimportCounts(reimport, choices) })
              : t('import.preview.confirmCommit', { count: preview.test_cases })
          }
          confirmLabel={t('import.preview.commit')}
          pending={busy}
          onCancel={() => setConfirmCommit(false)}
          onConfirm={() => void handleCommit()}
        />
      )}
    </div>
  );
}
